<!--
  LDPipPopover -- L/D pip with Vuetify v-menu picker for the Colors section.

  Pebble: L/D pip: obvious switch UI (popover picker instead of cycle-on-click)
  Card: Product editor (hi-fi redesign) -- https://trello.com/c/Qlwx8mlT

  Replaces the cycle-on-click interaction model (PR #163/#174) with an
  explicit three-option picker:
    - Auto   -- clear manual override, let heuristic decide
    - Light  -- force light
    - Dark   -- force dark

  Props:
    color         {Object}       The color object { color_sku, hex, name }
    manualOverride {String|null} The current manual_ld value for this SKU
                                 ('light' | 'dark' | null). Null = Auto.

  Emits:
    update:manual-ld  ('light' | 'dark' | null)
      Null signals Auto (clear the manual override).
      Parent (ColorsPanel) is responsible for writing into manual_ld map.

  Interaction model:
    - Click or Enter/Space opens the menu (v-menu activator).
    - Clicking an option selects it, emits, and closes.
    - Escape closes without emitting (v-menu default).
    - Clicking outside closes without emitting (v-menu default).
    - Focus returns to the trigger on close (v-menu default).

  Layout intent (visual-bug fix 2026-06-17):
    Previously the component wrapped the pip in an inline-block host span.
    That broke the badge's `position: absolute` anchor when the consumer's
    tile had multi-line label content: the inline-block wrapper sat lower
    in the tile and dragged the absolute-positioned badge down with it.
    The wrapper ALSO scoped the popover panel under `overflow: hidden`
    parents (v-card-text), clipping it on bottom-row cards.

    Fix:
      1. The host span uses `display: contents` so it contributes NO
         layout box. The badge's `position: absolute` now anchors against
         the consumer's nearest positioned ancestor (.colors-tile, which
         is `position: relative`) regardless of label wrap.
      2. The picker is rendered via Vuetify v-menu, which teleports to
         body via the overlay layer, escaping any `overflow: hidden`
         parent. v-menu also auto-flips when near the viewport edge.
-->
<template>
    <span class="ld-pip-popover-host" @click.stop>
        <v-menu
            v-model="isOpen"
            location="bottom end"
            :close-on-content-click="false"
            :close-on-back="true"
        >
            <template #activator="{ props: activatorProps }">
                <span
                    v-bind="activatorProps"
                    data-testid="ld-pip-trigger"
                    class="ld-pip"
                    :class="{
                        'is-manual': manualOverride !== null && manualOverride !== undefined,
                        'is-ambiguous': pipState === 'ambiguous',
                        [`ld-${pipState}`]: true,
                    }"
                    :aria-label="triggerAriaLabel"
                    aria-haspopup="menu"
                    tabindex="0"
                    role="button"
                    @click.stop
                    @keydown.enter.prevent="openPopover"
                    @keydown.space.prevent="openPopover"
                    @keydown.escape.prevent="closePopover"
                >{{ pipText }}</span>
            </template>

            <div
                data-testid="ld-pip-popover"
                class="ld-pip-popover"
                role="menu"
                :aria-label="`Light/Dark for ${displayName}`"
                @click.stop
                @keydown.escape.prevent="closePopover"
            >
                <button
                    v-for="opt in options"
                    :key="opt.key"
                    :data-testid="`ld-pip-option-${opt.key}`"
                    type="button"
                    class="ld-pip-option"
                    :class="{ 'is-selected': selectedKey === opt.key }"
                    :aria-checked="selectedKey === opt.key"
                    role="menuitemradio"
                    @click.stop="selectOption(opt.value)"
                >
                    <span class="ld-pip-option-icon">{{ opt.icon }}</span>
                    <span class="ld-pip-option-label">{{ opt.label }}</span>
                </button>
            </div>
        </v-menu>
    </span>
</template>

<script setup>
import { ref, computed } from 'vue'
import { classifyLD } from '@/utils/luminance'
import { classifyByName } from '@/utils/colorNameHeuristic'

defineOptions({ inheritAttrs: true })

const props = defineProps({
    color: { type: Object, required: true },
    manualOverride: { type: String, default: null },
})

const emit = defineEmits(['update:manual-ld'])

// ---- local state ----

const isOpen = ref(false)

// ---- derived display state ----

const displayName = computed(() => props.color?.name || props.color?.color_sku || '')

function resolvedLDState() {
    // Manual override wins.
    if (props.manualOverride === 'light' || props.manualOverride === 'dark') {
        return props.manualOverride
    }
    // Hex-derived luminance.
    const fromHex = classifyLD(props.color?.hex)
    if (fromHex === 'light' || fromHex === 'dark') return fromHex
    // Name heuristic.
    const heuristic = classifyByName(props.color?.name)
    if (heuristic.ld === 'light' || heuristic.ld === 'dark') return heuristic.ld
    return 'ambiguous'
}

const pipState = computed(() => resolvedLDState())

const pipText = computed(() => {
    if (pipState.value === 'light') return 'L'
    if (pipState.value === 'dark') return 'D'
    return '?'
})

const triggerAriaLabel = computed(() => {
    const name = displayName.value
    const isManual = props.manualOverride !== null && props.manualOverride !== undefined
    if (pipState.value === 'ambiguous') {
        return `Set light/dark for ${name}`
    }
    const label = pipState.value === 'light' ? 'Light' : 'Dark'
    if (isManual) return `${label} (manually set) for ${name} -- click to change`
    return `${label} for ${name} -- click to change`
})

// ---- popover options ----

const options = [
    { key: 'auto', value: null, label: 'Auto', icon: '⟳' },
    { key: 'light', value: 'light', label: 'Light', icon: 'L' },
    { key: 'dark', value: 'dark', label: 'Dark', icon: 'D' },
]

const selectedKey = computed(() => {
    if (props.manualOverride === 'light') return 'light'
    if (props.manualOverride === 'dark') return 'dark'
    return 'auto'
})

// ---- open / close logic ----

function openPopover() {
    isOpen.value = true
}

function closePopover() {
    isOpen.value = false
}

function toggleOpen() {
    isOpen.value = !isOpen.value
}

function selectOption(value) {
    emit('update:manual-ld', value)
    closePopover()
}
</script>

<style scoped>
/*
 * Host wrapper -- display: contents so it contributes no layout box.
 * The badge's `position: absolute` then anchors against the consumer's
 * nearest positioned ancestor (e.g. .colors-tile) directly. Multi-line
 * label content in the tile no longer drags the badge down.
 *
 * Note: data-testid (forwarded from ColorsPanel for selector purposes)
 * still lands on this span thanks to inheritAttrs: true. CSS-wise the
 * span is invisible to layout.
 */
.ld-pip-popover-host {
    display: contents;
}

/*
 * Pip trigger -- visual unchanged from the previous custom popover. The
 * `position: absolute` anchors against the consumer's positioned
 * ancestor (the tile), NOT against the host span (which is now
 * display: contents and has no layout box).
 */
.ld-pip {
    position: absolute;
    top: 2px;
    right: 2px;
    min-width: 16px;
    height: 16px;
    padding: 0 3px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(0, 0, 0, 0.25);
    color: rgba(0, 0, 0, 0.75);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
    line-height: 1;
    user-select: none;
    outline-offset: 2px;
}

.ld-pip:focus-visible {
    outline: 2px solid rgb(25, 118, 210);
}

.ld-pip.is-manual {
    border-style: dashed;
}

.ld-pip.is-ambiguous {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(0, 0, 0, 0.15);
    color: rgba(0, 0, 0, 0.5);
}

/*
 * Popover panel -- now rendered inside the Vuetify v-menu overlay layer
 * (teleported to body), so it escapes any `overflow: hidden` parent.
 * v-menu also handles auto-flip near viewport edges, fixing the bottom-row
 * clipping bug. Style preserved from the previous custom popover so the
 * visual stays identical.
 */
.ld-pip-popover {
    display: flex;
    flex-direction: column;
    background: var(--tf-bg, white);
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 90px;
    padding: 4px;
    gap: 1px;
}

/*
 * Option button -- compact row with icon + label. Taller hit target for touch.
 */
.ld-pip-option {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    padding: 5px 8px;
    border: none;
    background: transparent;
    border-radius: 4px;
    font-size: 12px;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
    text-align: left;
    width: 100%;
    color: rgba(0, 0, 0, 0.8);
    transition: background 80ms ease;
}

.ld-pip-option:hover,
.ld-pip-option:focus-visible {
    background: rgba(25, 118, 210, 0.08);
    outline: none;
}

.ld-pip-option.is-selected {
    background: rgba(25, 118, 210, 0.12);
    color: rgb(25, 118, 210);
    font-weight: 600;
}

.ld-pip-option-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.06);
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    flex: 0 0 auto;
}

.ld-pip-option.is-selected .ld-pip-option-icon {
    background: rgba(25, 118, 210, 0.15);
    color: rgb(25, 118, 210);
}

.ld-pip-option-label {
    flex: 1 1 auto;
    line-height: 1.2;
}
</style>
